import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, ArrowRight, Building2, CheckCircle2, CircleDollarSign, Clipboard, CreditCard, Landmark, LoaderCircle, RefreshCw, ShieldCheck, UsersRound } from "lucide-react";
import { createChapterDuesAchMemberLink, createPublicChapterDuesAchSetupSession, getChapterDuesAch, getPublicChapterDuesAch, refreshChapterDuesAchAccount, refreshChapterDuesAchMember, refreshPublicChapterDuesAch, revokeChapterDuesAchMember, revokePublicChapterDuesAch, saveChapterDuesAchPolicy, setChapterDuesAchCollectionsEnabled, setChapterDuesAchEnabled, setChapterDuesAchMemberAutomaticCollections, setChapterDuesCardPaymentsAllowed, startChapterDuesAchOnboarding } from "../api";
import type { ChapterDuesAchData, ChapterDuesFirstMonthPolicy, PublicChapterDuesAchData } from "../types";

const money = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});
const authLabels: Record<string, string> = {
  NOT_INVITED: "Not invited",
  INVITED: "Link prepared",
  PENDING: "Setup started",
  READY: "Payment method authorized",
  REQUIRES_UPDATE: "Needs update",
  DISABLED: "Disabled",
};
const paymentMethodLabel = (authorization: { paymentMethodType?: "us_bank_account" | "card"; bankName?: string; cardBrand?: string; cardFunding?: string; last4?: string }) => {
  if (!authorization.last4) return "No payment details stored here";
  if (authorization.paymentMethodType === "card") return `${authorization.cardBrand || "Card"} ${authorization.cardFunding || "card"} •••• ${authorization.last4}`;
  return `${authorization.bankName || "Bank account"} •••• ${authorization.last4}`;
};

export function ChapterDuesAchPage({ readOnly = false }: { readOnly?: boolean }) {
  const [data, setData] = useState<ChapterDuesAchData>();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [search, setSearch] = useState("");
  const [copied, setCopied] = useState("");
  const [collectionNotice, setCollectionNotice] = useState("");
  const [policy, setPolicy] = useState<ChapterDuesFirstMonthPolicy>("START_NEXT_MONTH");
  const stripeReturn = new URLSearchParams(window.location.search).get("chapterDuesAch");
  const load = async () => {
    try {
      const next = await getChapterDuesAch();
      setData(next);
      setPolicy(next.account.firstMonthPolicy);
      setError("");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Unable to load Chapter Dues ACH.");
    }
  };
  useEffect(() => {
    void load();
  }, []);
  const members = useMemo(() => data?.members.filter((member) => member.memberName.toLowerCase().includes(search.toLowerCase()) || member.email?.toLowerCase().includes(search.toLowerCase())) ?? [], [data, search]);
  const onboard = async () => {
    setBusy("onboarding");
    try {
      const result = await startChapterDuesAchOnboarding();
      window.location.assign(result.url);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Unable to open Stripe onboarding.");
      setBusy("");
    }
  };
  const refresh = async () => {
    setBusy("refresh");
    try {
      await refreshChapterDuesAchAccount();
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Unable to refresh Stripe status.");
    } finally {
      setBusy("");
    }
  };
  const toggle = async (enabled: boolean) => {
    setBusy("toggle");
    try {
      await setChapterDuesAchEnabled(enabled);
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Unable to change Chapter Dues ACH.");
    } finally {
      setBusy("");
    }
  };
  const savePolicy = async () => {
    setBusy("policy");
    try {
      await saveChapterDuesAchPolicy(policy);
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Unable to save the Chapter dues policy.");
    } finally {
      setBusy("");
    }
  };
  const toggleCollections = async (enabled: boolean) => {
    if (!enabled && !window.confirm("Pause all automatic drafts? This removes every Member from automatic drafts. No Member will be collected again until the Secretary/Treasurer includes that Member again.")) return;
    setBusy("collections");
    try {
      await setChapterDuesAchCollectionsEnabled(enabled);
      setCollectionNotice(enabled ? "Automatic drafts are active." : "All automatic drafts are paused. Each Member must be included again before another automatic collection can run.");
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Unable to change automatic collection authorization.");
    } finally {
      setBusy("");
    }
  };
  const toggleCards = async (allowed: boolean) => {
    setBusy("cards");
    try {
      await setChapterDuesCardPaymentsAllowed(allowed);
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Unable to change the Chapter's card-payment option.");
    } finally {
      setBusy("");
    }
  };
  const copyLink = async (memberId: string) => {
    setBusy(memberId);
    try {
      const result = await createChapterDuesAchMemberLink(memberId);
      await navigator.clipboard.writeText(result.url);
      setCopied(memberId);
      window.setTimeout(() => setCopied(""), 3000);
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Unable to prepare that Member link.");
    } finally {
      setBusy("");
    }
  };
  const revoke = async (memberId: string) => {
    const reason = window.prompt("Why is this Member payment authorization being stopped?");
    if (!reason) return;
    setBusy(`revoke-${memberId}`);
    try {
      await revokeChapterDuesAchMember(memberId, reason);
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Unable to stop that Member authorization.");
    } finally {
      setBusy("");
    }
  };
  const refreshMember = async (memberId: string) => {
    setBusy(`refresh-${memberId}`);
    try {
      await refreshChapterDuesAchMember(memberId);
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Unable to check that Member authorization.");
    } finally {
      setBusy("");
    }
  };
  const toggleMemberCollections = async (member: ChapterDuesAchData["members"][number], enabled: boolean) => {
    if (enabled) {
      const dueNow = member.amountDue > 0;
      const confirmation = dueNow
        ? `Include ${member.memberName} in automatic drafts? ${money.format(member.amountDue)} is currently due${member.duePeriods.length ? ` for ${member.duePeriods.join(", ")}` : ""}. The platform may submit that amount on the next collection run; it will not wait for the next future billing date.`
        : `Include ${member.memberName} in automatic drafts? No payment is due now. The platform will automatically collect the next dues obligation when its scheduled due date arrives.`;
      if (!window.confirm(confirmation)) return;
    }
    setBusy(`collections-${member.memberId}`);
    try {
      await setChapterDuesAchMemberAutomaticCollections(member.memberId, enabled);
      setCollectionNotice(enabled
        ? `${member.memberName} is included in automatic drafts. ${member.amountDue > 0 ? `${money.format(member.amountDue)} is eligible for the next collection run.` : "Nothing was charged now; the next draft waits for its scheduled due date."}`
        : `${member.memberName} was removed from automatic drafts.`);
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Unable to change automatic drafts for that Member.");
    } finally {
      setBusy("");
    }
  };
  if (!data && !error)
    return (
      <div className="load-state">
        <LoaderCircle className="spin" />
        <strong>Preparing Chapter dues collection…</strong>
      </div>
    );
  if (!data)
    return (
      <div className="error-banner">
        <AlertTriangle />
        {error}
        <button onClick={() => void load()}>Try again</button>
      </div>
    );
  const ready = data.account.status === "READY";
  return (
    <>
      <div className="page-heading">
        <div>
          <p className="eyebrow">Optional Chapter finance service</p>
          <h1>Collect Chapter dues automatically.</h1>
          <p>ACH bank payments are the default. Your Chapter may optionally offer Stripe credit and debit cards, while every payment still goes directly to the Chapter-owned Stripe account.</p>
        </div>
        <button className="secondary-button" onClick={() => void load()}>
          <RefreshCw />
          Refresh
        </button>
      </div>
      {stripeReturn === "return" ? (
        <div className="notice-banner">
          <CheckCircle2 />
          <span>
            <strong>You returned safely from Stripe.</strong>
            <small>Use Check Stripe Status below. Stripe may take a moment to finish verifying the Chapter account.</small>
          </span>
        </div>
      ) : null}
      {stripeReturn === "refresh" ? (
        <div className="notice-banner">
          <AlertTriangle />
          <span>
            <strong>Stripe needs the Chapter to finish or update its information.</strong>
            <small>Continue Stripe Setup below to complete the remaining requirements.</small>
          </span>
        </div>
      ) : null}
      {error ? (
        <div className="error-banner">
          <AlertTriangle />
          {error}
          <button onClick={() => setError("")}>Dismiss</button>
        </div>
      ) : null}
      <section className="chapter-ach-boundary">
        <ShieldCheck />
        <div>
          <strong>Your Chapter owns this money and this Stripe account.</strong>
          <p>This is separate from BNI Tampa Bay’s Chapter Success payment. The workflow never routes, transfers, or pays Member dues to BNI Tampa Bay. Members are charged only their Chapter dues; your Chapter receives the funds and absorbs Stripe’s processing, return, dispute, and verification fees.</p>
        </div>
      </section>
      <section className="chapter-ach-steps">
        <article className={data.account.status !== "NOT_STARTED" ? "complete" : "active"}>
          <span>1</span>
          <div>
            <strong>Create the Chapter-owned Stripe account</strong>
            <p>The S/T completes Stripe’s identity, tax, business, and Chapter checking-account payout setup.</p>
            {!readOnly ? (
              <button className="primary-button" disabled={!data.configuration.enabled || busy === "onboarding"} onClick={() => void onboard()}>
                {busy === "onboarding" ? <LoaderCircle className="spin" /> : <Building2 />}
                {data.account.status === "NOT_STARTED" ? "Start Secure Stripe Setup" : "Continue Stripe Setup"}
              </button>
            ) : null}
          </div>
        </article>
        <article className={ready ? "complete" : data.account.status === "ONBOARDING" || data.account.status === "RESTRICTED" ? "active" : ""}>
          <span>2</span>
          <div>
            <strong>Verify ACH and payouts</strong>
            <p>Stripe must approve ACH and payouts before this program can be enabled. Card capability is needed only if the Chapter later turns on the optional card setting.</p>
            {data.account.status !== "NOT_STARTED" && !readOnly ? (
              <button className="secondary-button" disabled={busy === "refresh"} onClick={() => void refresh()}>
                <RefreshCw className={busy === "refresh" ? "spin" : ""} />
                Check Stripe Status
              </button>
            ) : null}
          </div>
        </article>
        <article className={data.account.policyAcceptedAt ? "complete" : ready ? "active" : ""}>
          <span>3</span>
          <div>
            <strong>Choose and accept the Chapter collection policy</strong>
            <p>Set how a new Member’s first partial month is handled. A paid month is never refunded or prorated after a Member leaves.</p>
          </div>
        </article>
        <article className={data.account.enabled ? "complete" : ready && data.account.policyAcceptedAt ? "active" : ""}>
          <span>4</span>
          <div>
            <strong>Open Member enrollment</strong>
            <p>This separate switch permits private Member payment links. It does not authorize automatic collections.</p>
            {ready && data.account.policyAcceptedAt && !readOnly ? (
              <button className={data.account.enabled ? "secondary-button" : "primary-button"} disabled={busy === "toggle"} onClick={() => void toggle(!data.account.enabled)}>
                {data.account.enabled ? "Close Member Enrollment" : "Open Member Enrollment"}
              </button>
            ) : null}
          </div>
        </article>
        <article className={data.summary.members > 0 && data.summary.ready === data.summary.members ? "complete" : data.account.enabled ? "active" : ""}>
          <span>5</span>
          <div>
            <strong>Give each Member their private link</strong>
            <p>Members review the dated terms, then use Stripe’s hosted page to authorize a bank account. Credit and debit cards appear only when this Chapter turns on the optional card setting. Links expire in seven days, and the Chapter—not the Member—absorbs Stripe’s fees.</p>
          </div>
        </article>
        <article className={data.account.collectionsEnabled ? "complete" : data.account.enabled ? "active" : ""}>
          <span>6</span>
          <div>
            <strong>Include each Member who should be drafted</strong>
            <p><strong>Include in Automatic Drafts</strong> is the final Chapter action for that Member. The first inclusion automatically activates Chapter collections; there is no second Chapter-wide authorization step. {data.summary.selectedForAutomaticDrafts} currently included. The platform-wide emergency safety switch remains independent.</p>
            {data.account.collectionsEnabled && !readOnly ? (
              <button className="secondary-button" disabled={busy === "collections"} onClick={() => void toggleCollections(false)}>
                Pause All Automatic Drafts
              </button>
            ) : null}
          </div>
        </article>
      </section>
      {collectionNotice ? (
        <div className="notice-banner">
          <CheckCircle2 />
          <span><strong>Automatic draft settings saved.</strong><small>{collectionNotice}</small></span>
        </div>
      ) : null}
      {data.account.status !== "NOT_STARTED" ? (
        <section className="panel chapter-ach-how">
          <div className="panel-title">
            <ShieldCheck />
            <div>
              <h2>New Member and drop policy</h2>
              <p>This policy is shown to every Member before Stripe setup and governs the first partial month.</p>
            </div>
          </div>
          <label>
            <strong>When a new Member joins during a month</strong>
            <select value={policy} disabled={readOnly || busy === "policy"} onChange={(event) => setPolicy(event.target.value as ChapterDuesFirstMonthPolicy)}>
              <option value="FULL_MONTH">Collect the full month</option>
              <option value="PRORATE_REMAINING_WEEKS">Prorate by remaining weekly meetings</option>
              <option value="START_NEXT_MONTH">Charge nothing now; begin next month</option>
            </select>
          </label>
          <p>
            <strong>Drop rule:</strong> a completed draft pays the entire coming month. If a Member leaves during that month, no refund or mid-month proration is issued. Future automatic drafts stop when the Member becomes inactive.
          </p>
          {!readOnly ? (
            <button className="secondary-button" disabled={busy === "policy" || (policy === data.account.firstMonthPolicy && Boolean(data.account.policyAcceptedAt))} onClick={() => void savePolicy()}>
              {busy === "policy" ? <LoaderCircle className="spin" /> : <CheckCircle2 />}
              Save and Accept Chapter Policy
            </button>
          ) : null}
        </section>
      ) : null}
      {data.account.enabled ? (
        <section className="panel chapter-ach-how">
          <div className="panel-title">
            <CreditCard />
            <div>
              <h2>Optional credit and debit cards</h2>
              <p>ACH is the default. Leave cards off if the Chapter wants to keep using an existing merchant account.</p>
            </div>
          </div>
          <p>
            <strong>{data.account.cardPaymentsAllowed ? "Cards are available in new Member Stripe setup sessions." : "Only ACH bank authorization is offered through this platform."}</strong> When cards are on, the Member is charged only dues and the Chapter absorbs Stripe’s processing fees. Turning cards off prevents new card authorizations and pauses automatic collection from existing card authorizations; it does not affect ACH.
          </p>
          {!readOnly ? (
            <button className={data.account.cardPaymentsAllowed ? "secondary-button" : "primary-button"} disabled={busy === "cards" || !data.account.cardPaymentsEnabled} onClick={() => void toggleCards(!data.account.cardPaymentsAllowed)}>
              {busy === "cards" ? <LoaderCircle className="spin" /> : <CreditCard />}
              {data.account.cardPaymentsAllowed ? "Turn Off Stripe Card Payments" : data.account.cardPaymentsEnabled ? "Offer Stripe Card Payments" : "Card Capability Pending in Stripe"}
            </button>
          ) : null}
        </section>
      ) : null}
      <details className="chapter-ach-guide">
        <summary>Open the complete setup instructions</summary>
        <ol>
          <li>
            Choose <strong>Start Secure Stripe Setup</strong> and enter the Chapter’s legal/business information in Stripe—not a Member’s personal business information.
          </li>
          <li>Choose the Chapter checking account that should receive Member dues and complete every identity, tax, and bank-verification item Stripe requests.</li>
          <li>
            Return here and choose <strong>Check Stripe Status</strong>. Do not continue until ACH and payouts are approved.
          </li>
          <li>Review and accept the Chapter’s first-month policy. Every paid month covers the full coming month and is not refunded or reprorated if a Member leaves during that month.</li>
          <li>
            Choose <strong>Open Member Enrollment</strong>. This permits private Member links but does not authorize a collection.
          </li>
          <li>
            Leave <strong>Optional credit and debit cards</strong> off to use ACH only, or turn it on after Stripe approves card capability. Chapters with another merchant account may keep cards off permanently.
          </li>
          <li>
            For each Member, choose <strong>Copy Private Setup Link</strong> and send it only to that Member. Create a new link if the seven-day link expires.
          </li>
          <li>The Member reviews the exact dated terms, explicitly accepts them, then chooses a bank account. Credit and debit cards appear only when the Chapter enabled that option. The S/T never enters or sees a full account or card number.</li>
          <li>
            Confirm the Member now shows <strong>Payment method authorized</strong>. The dashboard will carry every unpaid dues period until that exact period succeeds.
          </li>
          <li>For every authorized Member who should be drafted, choose <strong>Include in Automatic Drafts</strong>. That is the Chapter’s final collection action for that Member, and including the first Member automatically activates Chapter collections. A Chapter can safely begin with one Member without enabling anyone else.</li>
          <li>If the Member already has dues due or past due, the confirmation shows the exact current amount and the platform may submit it on the next collection run. When nothing is due, no payment is created and the platform waits for the next scheduled due date. The independent platform-wide emergency safety switch is still required before any collection can run.</li>
          <li>Each due period is collected separately, paid directly into the Chapter’s Stripe balance, and tracked here through processing, success, failure, reconciliation, and limited retry.</li>
          <li>Use the Stripe payout that later appears in the Chapter’s selected Plaid account to reconcile gross Chapter Dues income, the Chapter’s Stripe processing-fee expense, and the net deposit.</li>
        </ol>
      </details>
      {!data.configuration.globalEnabled ? (
        <div className="notice-banner">
          <ShieldCheck />
          <span>
            <strong>The new module is installed in safe mode.</strong>
            <small>Stripe onboarding and collection remain unavailable until the dedicated Connect credential, webhook, and feature switch are deliberately enabled.</small>
          </span>
        </div>
      ) : null}
      {data.configuration.globalEnabled && !data.configuration.availableToChapter ? (
        <div className="notice-banner">
          <ShieldCheck />
          <span>
            <strong>This Chapter is not in the controlled pilot.</strong>
            <small>Only specifically approved pilot Chapters can open Stripe enrollment. Other Chapters remain fully blocked.</small>
          </span>
        </div>
      ) : null}
      {data.configuration.enabled && data.configuration.mode === "test" ? (
        <div className="notice-banner">
          <ShieldCheck />
          <span>
            <strong>Stripe test mode</strong>
            <small>This pilot uses fictional Stripe test data. It cannot debit or pay out real money.</small>
          </span>
        </div>
      ) : null}
      {data.configuration.enabled && !data.configuration.collectionsEnabled ? (
        <div className="notice-banner">
          <ShieldCheck />
          <span>
            <strong>Enrollment-only mode</strong>
            <small>Chapters may complete setup and Members may authorize payment methods, but the platform-wide collection switch is off. No automatic payment can occur.</small>
          </span>
        </div>
      ) : null}
      <section className="chapter-ach-summary">
        <article>
          <UsersRound />
          <span>
            <small>Members authorized</small>
            <strong>
              {data.summary.ready} of {data.summary.members}
            </strong>
          </span>
        </article>
        <article>
          <CircleDollarSign />
          <span>
            <small>Outstanding Member dues</small>
            <strong>{money.format(data.summary.outstandingAmount)}</strong>
          </span>
        </article>
        <article>
          <RefreshCw />
          <span>
            <small>Payments processing</small>
            <strong>{data.summary.processing}</strong>
          </span>
        </article>
        <article>
          <AlertTriangle />
          <span>
            <small>Failed payments</small>
            <strong>{data.summary.failed}</strong>
          </span>
        </article>
      </section>
      <section className="panel chapter-ach-members">
        <div className="panel-title">
          <CreditCard />
          <div>
            <h2>Member payment authorizations and balances</h2>
            <p>Copy a private link and send it directly to the correct Member. Never post these links publicly.</p>
          </div>
        </div>
        <input className="chapter-ach-search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Find a Member by name or email" aria-label="Find a Member" />
        <div className="chapter-ach-member-list">
          {members.map((member) => (
            <article key={member.memberId}>
              <div>
                <strong>{member.memberName}</strong>
                <small>{member.email || "No email on file"}</small>
              </div>
              <span>
                <small>Balance</small>
                <b className={member.amountDue ? "amount-due" : ""}>{money.format(member.amountDue)}</b>
                <em>{member.duePeriods.join(", ") || "Current"}</em>
              </span>
              <span>
                <small>Authorization</small>
                <b>{authLabels[member.authorization.status]}</b>
                <em>{paymentMethodLabel(member.authorization)}</em>
                <em>{member.authorization.automaticCollectionsEnabled ? "Included in automatic drafts" : "Not included in automatic drafts"}</em>
              </span>
              {!readOnly ? (
                <div>
                  <button className="secondary-button" disabled={!data.account.enabled || busy === member.memberId} onClick={() => void copyLink(member.memberId)}>
                    {busy === member.memberId ? <LoaderCircle className="spin" /> : copied === member.memberId ? <CheckCircle2 /> : <Clipboard />}
                    {copied === member.memberId ? "Link Copied" : member.authorization.status === "READY" ? "Create Update Link" : "Copy Private Setup Link"}
                  </button>
                  {member.authorization.status === "READY" ? (
                    <>
                      <button className={member.authorization.automaticCollectionsEnabled ? "secondary-button" : "primary-button"} disabled={busy === `collections-${member.memberId}`} onClick={() => void toggleMemberCollections(member, !member.authorization.automaticCollectionsEnabled)}>
                        {member.authorization.automaticCollectionsEnabled ? "Remove from Automatic Drafts" : "Include in Automatic Drafts"}
                      </button>
                      <button className="secondary-button" disabled={busy === `revoke-${member.memberId}`} onClick={() => void revoke(member.memberId)}>Stop Payment Method</button>
                    </>
                  ) : member.authorization.status === "PENDING" || member.authorization.status === "REQUIRES_UPDATE" ? (
                    <button className="secondary-button" disabled={busy === `refresh-${member.memberId}`} onClick={() => void refreshMember(member.memberId)}>
                      <RefreshCw className={busy === `refresh-${member.memberId}` ? "spin" : ""} />
                      Check Authorization Status
                    </button>
                  ) : null}
                </div>
              ) : null}
            </article>
          ))}
        </div>
      </section>
      <section className="panel chapter-ach-payments">
        <div className="panel-title">
          <CircleDollarSign />
          <div>
            <h2>Automatic payment history</h2>
            <p>Each row is tied to one Member and one dues period. Members are never charged more than the dues amount shown.</p>
          </div>
        </div>
        {data.payments.length ? (
          <div>
            {data.payments.map((payment) => {
              const member = data.members.find((candidate) => candidate.memberId === payment.memberId);
              return (
                <article key={payment.id}>
                  <span>
                    <strong>{member?.memberName || "Member"}</strong>
                    <small>
                      {payment.period} dues · {payment.paymentMethodType === "card" ? "Card" : "ACH"} · attempt {payment.attemptCount}
                    </small>
                  </span>
                  <b>{money.format(payment.amount)}</b>
                  <em className={`chapter-ach-payment-status status-${payment.status.toLowerCase()}`}>{payment.status === "SUCCEEDED" ? "Paid" : payment.status === "PROCESSING" ? "Processing" : payment.status === "UNKNOWN" ? "Needs Stripe reconciliation" : payment.status === "FAILED" ? "Failed" : payment.status === "CANCELED" ? "Canceled" : "Created"}</em>
                  <small>{payment.processingFee == null ? (payment.nextRetryAt ? `Next retry ${new Date(payment.nextRetryAt).toLocaleDateString()}` : payment.settledAt ? "Stripe fee reconciliation pending" : payment.initiatedAt ? `Started ${new Date(payment.initiatedAt).toLocaleDateString()}` : "Not submitted") : `Chapter fee ${money.format(payment.processingFee)} · net ${money.format(payment.netAmount ?? payment.amount - payment.processingFee)}`}</small>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="chapter-ach-empty">
            <CheckCircle2 />
            <strong>No automatic Chapter dues payments yet.</strong>
            <span>Payment history will appear after Members authorize a payment method and collections are deliberately enabled.</span>
          </div>
        )}
      </section>
      <section className="panel chapter-ach-how">
        <div className="panel-title">
          <ShieldCheck />
          <div>
            <h2>How automatic accounting works</h2>
            <p>The Chapter dues schedule remains the source of each Member’s balance.</p>
          </div>
        </div>
        <ol>
          <li>
            <strong>The platform creates the obligation.</strong> Monthly or quarterly dues become owed on the Chapter’s configured due date, including prorated and prior unpaid periods.
          </li>
          <li>
            <strong>Only authorized Members are charged.</strong> After the due date, the platform sends one direct payment per unpaid period to that Chapter’s connected Stripe account using the Member’s chosen bank account or card.
          </li>
          <li>
            <strong>The Member pays only dues.</strong> No processing surcharge or convenience fee is added. Stripe deducts its fee from the Chapter’s proceeds.
          </li>
          <li>
            <strong>Success marks that exact period paid.</strong> Stripe’s signed webhook updates the Member balance and payment history. Processing remains visibly pending until Stripe confirms success.
          </li>
          <li>
            <strong>Failures stay outstanding.</strong> Failed payments return the exact period to owed status, preserve the reason, and either retry within the controlled limit or ask the Member to update their card.
          </li>
          <li>
            <strong>Stripe pays the Chapter.</strong> The history records gross dues, the Chapter’s Stripe expense, and the net amount. The payout later appears through Plaid for reconciliation.
          </li>
        </ol>
      </section>
      <section className="chapter-ach-fees">
        <div>
          <p className="eyebrow">Chapter-paid processing</p>
          <h2>Members always pay only their Chapter dues.</h2>
          <p>Stripe pricing can change and is confirmed in the Chapter’s own Stripe Dashboard. The S/T platform never adds a Member-facing processing fee.</p>
        </div>
        <article>
          <strong>Dues only</strong>
          <span>Member charge</span>
          <small>ACH, credit, or debit</small>
        </article>
        <article>
          <strong>Chapter expense</strong>
          <span>Stripe processing fee</span>
          <small>never added to the Member</small>
        </article>
        <article>
          <strong>Automatic</strong>
          <span>gross, fee, and net tracking</span>
          <small>ready for payout reconciliation</small>
        </article>
      </section>
    </>
  );
}

export function MemberChapterDuesAchPage({ token }: { token: string }) {
  const [data, setData] = useState<PublicChapterDuesAchData>();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [revoked, setRevoked] = useState(false);
  const setupResult = new URLSearchParams(window.location.search).get("setup");
  useEffect(() => {
    let canceled = false;
    let timer: number | undefined;
    const loadAuthorization = async (attempt = 0) => {
      try {
        if (setupResult === "success") await refreshPublicChapterDuesAch(token);
        const next = await getPublicChapterDuesAch(token);
        if (canceled) return;
        setData(next);
        setError("");
        if (setupResult === "success" && next.authorization.status === "PENDING" && attempt < 4) {
          timer = window.setTimeout(() => void loadAuthorization(attempt + 1), 1500);
        }
      } catch (cause) {
        if (!canceled) setError(cause instanceof Error ? cause.message : "This secure link could not be opened.");
      }
    };
    void loadAuthorization();
    return () => { canceled = true; if (timer) window.clearTimeout(timer); };
  }, [setupResult, token]);
  const connect = async () => {
    if (!data || !accepted) return;
    setBusy(true);
    try {
      window.location.assign((await createPublicChapterDuesAchSetupSession(token, data.termsVersion)).url);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Unable to open secure payment setup.");
      setBusy(false);
    }
  };
  const revoke = async () => {
    if (!window.confirm("Stop this Chapter dues payment authorization? Future automatic payments will stop until you authorize again.")) return;
    setBusy(true);
    try {
      await revokePublicChapterDuesAch(token);
      setData((current) =>
        current
          ? {
              ...current,
              authorization: { ...current.authorization, status: "DISABLED" },
            }
          : current,
      );
      setRevoked(true);
      setAccepted(false);
      setError("");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Unable to revoke this authorization.");
    } finally {
      setBusy(false);
    }
  };
  if (!data && !error)
    return (
      <div className="member-ach-public">
        <div className="load-state">
          <LoaderCircle className="spin" />
          <strong>Opening your secure Chapter dues authorization…</strong>
        </div>
      </div>
    );
  if (!data)
    return (
      <div className="member-ach-public">
        <div className="member-ach-card">
          <AlertTriangle />
          <h1>This private link is not available.</h1>
          <p>{error}</p>
          <p>Ask your Chapter Secretary/Treasurer to create a new seven-day link.</p>
        </div>
      </div>
    );
  const ready = data.authorization.status === "READY";
  return (
    <div className="member-ach-public">
      <main className="member-ach-card">
        <div className="member-ach-brand">BNI Tampa Bay</div>
        <p className="eyebrow">{data.chapterName} Chapter dues</p>
        <h1>{revoked ? "Your payment authorization is stopped." : ready ? "Your payment method is ready." : `Hello, ${data.memberName}.`}</h1>
        <p>{revoked ? "Future automatic payments are stopped. Ask your Chapter Secretary/Treasurer for a new private link if you want to authorize again." : ready ? `Stripe has securely authorized ${paymentMethodLabel(data.authorization)}.` : data.cardPaymentsAllowed ? "Your Chapter uses Stripe to collect its own Member dues directly into its own Chapter account. You may choose a bank account, credit card, or debit card." : "Your Chapter uses Stripe ACH to collect its own Member dues directly into its own Chapter account. This setup authorizes your bank account."}</p>
        {revoked ? (
          <div className="notice-banner">
            <CheckCircle2 />
            <span>
              <strong>Your request was completed.</strong>
              <small>No future automatic Chapter dues payment can use the authorization you stopped.</small>
            </span>
          </div>
        ) : null}
        {setupResult === "success" && ready ? (
          <div className="notice-banner">
            <CheckCircle2 />
            <span>
              <strong>Your payment method is saved and ready.</strong>
              <small>Your Chapter can now include this method in automatic drafts. No payment was created by this setup step.</small>
            </span>
          </div>
        ) : null}
        {setupResult === "success" && !ready ? (
          <div className="notice-banner">
            <LoaderCircle className="spin" />
            <span>
              <strong>Stripe received your payment authorization.</strong>
              <small>We are checking the final status automatically. You do not need to submit the form again.</small>
            </span>
          </div>
        ) : null}
        {setupResult === "canceled" ? (
          <div className="notice-banner">
            <AlertTriangle />
            <span>
              <strong>No payment-method change was completed.</strong>
              <small>You can continue to Stripe again when you are ready. An existing authorization, if any, remains unchanged.</small>
            </span>
          </div>
        ) : null}
        {error ? (
          <div className="error-banner">
            <AlertTriangle />
            {error}
          </div>
        ) : null}
        <div className="member-ach-total">
          <span>
            <small>Current outstanding balance</small>
            <strong>{money.format(data.amountDue)}</strong>
          </span>
          <span>
            <small>Dues schedule</small>
            <strong>
              {money.format(data.duesAmount)} {data.frequency.toLowerCase()}
            </strong>
          </span>
        </div>
        <section>
          <ShieldCheck />
          <div>
            <strong>Terms for this authorization</strong>
            <p>{data.terms.draftTiming}</p>
            <p>{data.terms.dropPolicy}</p>
            <p>{data.terms.firstMonthPolicy}</p>
            <p>{data.terms.feePolicy}</p>
            <p>Outstanding and returned amounts remain due. Stripe—not BNI Tampa Bay or the S/T platform—collects and secures your bank or card information.</p>
          </div>
        </section>
        {!revoked ? (
          <>
            <label className="member-ach-privacy">
              <input type="checkbox" checked={accepted} onChange={(event) => setAccepted(event.target.checked)} /> I have reviewed and accept these Chapter dues payment terms.
            </label>
            <button className="primary-button member-ach-connect" disabled={busy || !accepted} onClick={() => void connect()}>
              {busy ? <LoaderCircle className="spin" /> : ready ? <RefreshCw /> : <CreditCard />}
              {ready ? "Change Authorized Payment Method" : "Continue to Secure Stripe Setup"}
              <ArrowRight />
            </button>
            {ready ? (
              <button className="secondary-button member-ach-connect" disabled={busy} onClick={() => void revoke()}>
                Revoke My Payment Authorization
              </button>
            ) : null}
          </>
        ) : null}
        <small className="member-ach-privacy">This link is private, expires, and is tied only to your Member record in {data.chapterName}. The platform retains only the method type, bank or card brand, last four digits, authorization status, and payment results.</small>
      </main>
    </div>
  );
}
